import type { ReactElement } from 'react';
import { Tooltip } from '@odo/shared-ui';
import type { TaskBodyProps } from '../index';
import { advanceWorkflow, type OutgoingAction } from '../../../../services/workQueue';
import type { HandBackTarget } from '../../../../services/finalReview';
import { useReviewActionRunner } from '../useReviewActionRunner';
import { HAND_BACK_TARGET_LABELS, handBackBlocker } from '../design-dx/handBackNotes';
import type { DraftNote } from './useDraftNotes';
import { findOdoForwardAction } from '../../odoForwardAction';

const BUTTON_CLASS =
    'px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export interface ReviewDecisionCopy {
    source: string;
    targetLabel: string;
    handBack: string;
    handingBack: string;
    handBackError: string;
    approve: string;
    approveError: string;
}

function withHint(button: ReactElement, hint: string | null): ReactElement {
    if (!hint) return button;
    return (
        <Tooltip content={hint} side="top">
            <span className="inline-block">{button}</span>
        </Tooltip>
    );
}

type ReviewDecisionFooterProps = Pick<TaskBodyProps, 'statusMeta' | 'onTaskComplete'> & {
    caseId: string;
    notes: DraftNote[];
    handBackAction: string;
    // Shown beside the buttons; null when the notes carry no section.
    handBackTarget: HandBackTarget | null;
    // Defaults to the step's first forward edge ODO may take.
    approveAction?: string;
    // Null while there is nothing to approve; approveHint says why.
    approve: ((action: OutgoingAction) => Promise<unknown>) | null;
    approveHint: string | null;
    copy: ReviewDecisionCopy;
};

// Notes send the case back to Clinical through the hand-back action; none lets it approve.
export function ReviewDecisionFooter({
    caseId,
    statusMeta,
    onTaskComplete,
    notes,
    handBackAction: handBackId,
    handBackTarget: target,
    approveAction: approveId,
    approve,
    approveHint,
    copy
}: ReviewDecisionFooterProps) {
    const { progress, runAction } = useReviewActionRunner(caseId, onTaskComplete, undefined);
    const busy = progress.pending !== null || progress.succeeded !== null;
    const handBackAction = statusMeta.outgoingActions.find((a) => a.action === handBackId);
    const approveAction = findOdoForwardAction(statusMeta.outgoingActions, approveId);
    const handBackHint = handBackBlocker(notes);

    const handBack = (): void => {
        if (!handBackAction) return;
        const body = {
            notes: notes.map(({ section, text }) => ({
                ...(section && { section }),
                text: text.trim()
            }))
        };
        void runAction(
            handBackAction,
            () => advanceWorkflow(caseId, handBackAction.action, body),
            `${copy.source}.handBack`,
            copy.handBackError
        );
    };

    const runApprove = (): void => {
        if (!approveAction || !approve) return;
        void runAction(
            approveAction,
            () => approve(approveAction),
            `${copy.source}.approve`,
            copy.approveError
        );
    };

    const noteLabel = `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`;
    return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 flex items-center gap-2">
            {target && (
                <>
                    <span className="text-xs text-gray-700">
                        {copy.targetLabel}: <strong>{HAND_BACK_TARGET_LABELS[target]}</strong>
                    </span>
                    <span className="text-[11px] text-gray-400">from where the notes are</span>
                </>
            )}
            <span className="flex-1" />
            {withHint(
                <button
                    type="button"
                    onClick={handBack}
                    disabled={busy || !handBackAction || handBackHint !== null}
                    className={`${BUTTON_CLASS} border border-red-500 text-red-500 bg-white hover:bg-red-50`}
                >
                    {progress.pending === handBackAction?.action
                        ? copy.handingBack
                        : `${copy.handBack} · ${noteLabel}`}
                </button>,
                notes.length > 0 ? handBackHint : null
            )}
            {withHint(
                <button
                    type="button"
                    onClick={runApprove}
                    disabled={busy || !approveAction || !approve || approveHint !== null}
                    className={`${BUTTON_CLASS} bg-gray-800 text-white hover:bg-gray-900`}
                >
                    {progress.pending === approveAction?.action ? 'Approving…' : copy.approve}
                </button>,
                approveHint
            )}
        </div>
    );
}
